import * as React from 'react'
import { AlertCircle } from 'lucide-react'
import { API_BASE_URL, isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'
import { isFramed } from '@/signing/frame'
import type { DocumentDto } from '@lib/dto/envelope.dto'
import type { EmbedChrome } from '@lib/dto/embed-message.dto'
import { accentTheme } from '@lib/helpers/accent-theme'
import type { HexColor } from '@lib/validators/tenant.validator'
import { useResolvedTheme } from '@/theme/ThemeProvider'
import { useAccentTheme } from './accent'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export function requestErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    return error.body.message ?? `Request failed (status ${error.status}).`
  }
  return toErrorMessage(error)
}

interface SignShellProps {
  brand: string
  /** The organization's stored logo (a path on the API), shown beside its name. */
  logoSrc?: string | null | undefined
  title: string
  /**
   * A test envelope: a banner says it isn't legally binding, on every signer
   * and answerer page. With the host's chrome, a badge above the content says
   * it instead (a page that places the badge itself passes false).
   */
  test?: boolean
  /** The organization's accent: buttons, links, focus, and a line under the header (see useAccentTheme). */
  accentColor?: HexColor | null | undefined
  /** `host`: an embed whose host shows the title, so no header or banner (see embedChromeSchema). */
  chrome?: EmbedChrome | undefined
  children: React.ReactNode
}

export function SignShell({ brand, logoSrc, title, test = false, accentColor, chrome = 'full', children }: SignShellProps) {
  useAccentTheme(accentColor, useResolvedTheme())
  const themed = accentTheme(accentColor) !== null
  if (chrome === 'host') {
    return (
      <div className={cn('bg-background', !isFramed() && 'min-h-svh')}>
        <main className="p-4">
          {test ? (
            <div className="mx-auto mb-4 w-fit">
              <TestBadge />
            </div>
          ) : null}
          {children}
        </main>
      </div>
    )
  }
  return (
    // In a frame the page is as tall as its content, so a host sizing the
    // frame to the reported height can shrink it as well as grow it.
    <div className={cn('bg-background', !isFramed() && 'min-h-svh')}>
      {test ? (
        <div role="status" className="border-b border-warning-border bg-warning-subtle px-4 py-2 text-center text-body font-medium text-warning">
          Test envelope: not legally binding. Nothing you sign here counts.
        </div>
      ) : null}
      <header className={cn('bg-card', themed ? 'border-b-2 border-primary' : 'border-b border-border')}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <p className="flex min-w-0 max-w-1/2 shrink-0 items-center gap-3 text-sm font-medium text-muted-foreground">
            {logoSrc ? <img src={`${API_BASE_URL}${logoSrc}`} alt="" className="h-7 w-auto max-w-40 shrink-0 object-contain" /> : null}
            <span className="truncate">{brand}</span>
          </p>
          <p className="min-w-0 truncate text-sm text-foreground">{title}</p>
        </div>
      </header>
      <main className="px-4 py-8">{children}</main>
    </div>
  )
}

/** Says a test envelope isn't legally binding, where the host's chrome replaces the banner. */
export function TestBadge() {
  return <Badge tone="warning">Test — not legally binding</Badge>
}

export function SignStatus({
  brand,
  logoSrc,
  accentColor,
  chrome,
  title,
  body,
}: {
  brand?: string
  logoSrc?: string | null | undefined
  accentColor?: HexColor | null | undefined
  chrome?: EmbedChrome | undefined
  title: string
  body: string
}) {
  return (
    <SignShell
      brand={brand ?? 'PixelSign'}
      logoSrc={logoSrc}
      accentColor={accentColor}
      title={title}
      chrome={chrome}
    >
      <Card className="mx-auto max-w-md">
        <CardHeader>
          <div className="mb-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
      </Card>
    </SignShell>
  )
}

export function DocumentToc({
  documents,
  onSelect,
}: {
  documents: readonly Pick<DocumentDto, 'id' | 'title' | 'presentationOrder'>[]
  onSelect: (id: string) => void
}) {
  const ordered = [...documents].sort(
    (a, b) => a.presentationOrder - b.presentationOrder,
  )
  return (
    <ol className="space-y-1 text-sm">
      {ordered.map((document, index) => (
        <li key={document.id}>
          <button
            type="button"
            className="w-full rounded-md px-2 py-1.5 text-left hover:bg-muted"
            onClick={() => onSelect(document.id)}
          >
            <span className="mr-2 text-muted-foreground">{index + 1}.</span>
            {document.title}
          </button>
        </li>
      ))}
    </ol>
  )
}
